import { useState } from "react";
import { SIZES } from "@/lib/products";
import { Check, Plus, X } from "lucide-react";

export const ProductCard = ({ product, onAdd }) => {
  const [size, setSize] = useState("");
  const [color, setColor] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  const canAdd = size && color;

  const handleAdd = () => {
    if (!canAdd) return;
    onAdd({
      product_id: product.id,
      product_name: product.name,
      size,
      color: color.value,
      color_label: color.label,
      price: product.price,
    });
    setSize("");
    setColor(null);
  };

  const currentImage = color ? color.image_url : product.group_image;
  const currentAlt = color ? `${product.name} ${color.label}` : product.name;

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="flex flex-col bg-white rounded-3xl border-2 border-slate-200 p-4 sm:p-5 lg:h-full shadow-sm"
    >
      {/* Title + Price */}
      <div className="flex flex-col mb-3 shrink-0">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
          {product.name}
        </h2>
        <span className="font-display text-lg sm:text-xl font-bold text-amber-500">
          {product.price} kr
        </span>
      </div>

      {/* Product image - click to zoom */}
      <button
        type="button"
        data-testid={`product-image-${product.id}`}
        onClick={() => setZoomed(true)}
        className="relative mb-4 shrink-0 rounded-xl overflow-hidden bg-slate-50 aspect-[4/3] lg:aspect-auto lg:h-32 xl:h-40 cursor-zoom-in hover:opacity-90 active:scale-[0.98] transition-all"
      >
        <img src={currentImage} alt={currentAlt} className="w-full h-full object-contain" />
      </button>

      {zoomed && (
        <div
          data-testid={`zoom-modal-${product.id}`}
          onClick={() => setZoomed(false)}
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out"
        >
          <button
            type="button"
            data-testid={`zoom-close-${product.id}`}
            onClick={(e) => {
              e.stopPropagation();
              setZoomed(false);
            }}
            className="absolute top-5 right-5 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center active:scale-95 transition-all"
            aria-label="Luk"
          >
            <X size={26} strokeWidth={2.5} />
          </button>
          <img
            src={currentImage}
            alt={currentAlt}
            className="max-w-[95vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Size selector */}
      <div className="mb-4 shrink-0">
        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
          Størrelse
        </label>
        <div className="grid grid-cols-5 gap-1.5">
          {SIZES.map((s) => (
            <button
              key={s}
              data-testid={`size-${product.id}-${s}`}
              onClick={() => setSize(size === s ? "" : s)}
              className={`h-10 text-[11px] sm:text-xs xl:text-sm font-bold rounded-lg border-2 transition-all px-0.5 whitespace-nowrap ${
                size === s
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 active:scale-95"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Color selector */}
      <div className="mb-5 shrink-0">
        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
          Farve
        </label>
        <div className="flex gap-2 sm:gap-3">
          {product.colors.map((c) => {
            const selected = color?.value === c.value;
            return (
              <button
                key={c.value}
                data-testid={`color-${product.id}-${c.value}`}
                onClick={() => setColor(color?.value === c.value ? null : c)}
                className={`relative flex-1 h-12 sm:h-14 rounded-xl border-2 transition-all active:scale-95 ${
                  selected ? "border-slate-900 shadow-md" : "border-slate-200"
                }`}
                style={{ backgroundColor: c.hex }}
                aria-label={c.label}
              >
                {selected && (
                  <Check
                    className={`absolute inset-0 m-auto ${
                      c.value === "white" ? "text-slate-900" : "text-white"
                    }`}
                    size={22}
                    strokeWidth={3}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Add button */}
      <button
        data-testid={`add-to-cart-${product.id}`}
        onClick={handleAdd}
        disabled={!canAdd}
        className={`lg:mt-auto h-14 sm:h-16 w-full rounded-2xl font-display text-lg sm:text-xl font-bold flex items-center justify-center gap-2 transition-all shrink-0 ${
          canAdd
            ? "bg-slate-900 text-white active:scale-95 hover:bg-slate-800"
            : "bg-slate-100 text-slate-400 cursor-not-allowed"
        }`}
      >
        <Plus size={20} strokeWidth={3} />
        Tilføj
      </button>
    </div>
  );
};
